import React, { useState, useEffect, useCallback } from 'react';

import { API_ENDPOINTS, getCurrentBrand } from '../config/api';
import { hasLimitedBackofficeNavigation, isLemon8Enabled } from '../utils/featureFlags';
import {
    Box,
    Typography,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Button,
    IconButton,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    Switch,
    CircularProgress,
    Alert,
    Chip,
    Container,
    Stack,
    Avatar
} from '@mui/material';
import {
    Edit as EditIcon,
    Close as CloseIcon,
    Save as SaveIcon,
    Description as DescriptionIcon,
    Facebook as FacebookIcon,
    Google as GoogleIcon,
    Instagram as InstagramIcon,
    WhatsApp as WhatsAppIcon,
    MusicNote as TiktokIcon,
    AutoAwesome as XhsIcon,
    AutoAwesome as Lemon8Icon,
    Public as GenericIcon
} from '@mui/icons-material';

const getPlatformConfig = (platform) => {
    const p = platform?.toLowerCase() || '';
    if (p.includes('facebook')) return { icon: <FacebookIcon />, color: '#1877F2', bg: '#e7f3ff' };
    if (p.includes('google')) return { icon: <GoogleIcon />, color: '#DB4437', bg: '#fce8e6' };
    if (p.includes('instagram')) return { icon: <InstagramIcon />, color: '#E4405F', bg: '#fcece8' };
    if (p.includes('whatsapp')) return { icon: <WhatsAppIcon />, color: '#25D366', bg: '#e8f8f0' };
    if (p.includes('tiktok')) return { icon: <TiktokIcon />, color: '#000000', bg: '#e6e6e6' };
    if (p.includes('xiaohongshu') || p.includes('xhs')) return { icon: <XhsIcon />, color: '#FF2442', bg: '#ffebee' };
    if (p.includes('lemon8')) return { icon: <Lemon8Icon />, color: '#D97706', bg: '#fff7ed' };
    return { icon: <GenericIcon />, color: '#757575', bg: '#f5f5f5' };
};

const PlatformContent = () => {
    const brand = getCurrentBrand();
    const hidesButtonText = hasLimitedBackofficeNavigation(brand);
    const [contentList, setContentList] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [editDialogOpen, setEditDialogOpen] = useState(false);
    const [editingItem, setEditingItem] = useState(null);
    const [saving, setSaving] = useState(false);

    // Fetch content
    const fetchContent = useCallback(async () => {
        try {
            setLoading(true);
            setError(null);
            const response = await fetch(`${API_ENDPOINTS.PLATFORM_CONTENT}?brand=${brand}`);

            if (!response.ok) {
                throw new Error('Failed to fetch platform content');
            }

            const data = await response.json();
            const lemon8Allowed = isLemon8Enabled(brand);
            setContentList(data.filter((item) => {
                const platform = String(item.platform || '').toLowerCase();
                if (!lemon8Allowed && platform === 'lemon8') return false;
                if (hidesButtonText && platform === 'whatsapp') return false;
                return true;
            }));
        } catch (err) {
            console.error('Error fetching content:', err);
            setError(err.message);
        } finally {
            setLoading(false);
        }
    }, [brand, hidesButtonText]);

    useEffect(() => {
        fetchContent();
    }, [fetchContent]);

    // Handle edit click
    const handleEditClick = (item) => {
        setEditingItem({ ...item });
        setEditDialogOpen(true);
    };

    // Handle save edit
    const handleSave = async () => {
        try {
            setSaving(true);
            const response = await fetch(`${API_ENDPOINTS.PLATFORM_CONTENT}/${editingItem.id}?brand=${brand}`, {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(hidesButtonText
                    ? { content: editingItem.content, url: editingItem.url, is_active: editingItem.is_active }
                    : editingItem)
            });
            const result = await response.json();

            if (!response.ok) {
                throw new Error(result.error || 'Failed to update content');
            }

            // Refresh list
            await fetchContent();
            setEditDialogOpen(false);
            setEditingItem(null);
        } catch (err) {
            console.error('Error updating content:', err);
            setError(err.message);
        } finally {
            setSaving(false);
        }
    };

    return (
        <>
            <style>
                {`
          .card-overlay {
            opacity: 0;
            transition: opacity 0.3s ease;
          }
          .MuiCard-root:hover .card-overlay {
            opacity: 1;
          }
          @keyframes loading {
            0% { background-position: 200% 0; }
            100% { background-position: -200% 0; }
          }
          @keyframes float {
            0%, 100% { transform: translateY(0px) rotate(0deg); }
            25% { transform: translateY(-10px) rotate(1deg); }
            50% { transform: translateY(-20px) rotate(0deg); }
            75% { transform: translateY(-10px) rotate(-1deg); }
          }
          @keyframes shimmer {
            0% { transform: translateX(-100%); }
            100% { transform: translateX(100%); }
          }
          @keyframes shimmer-gradient {
            0% { background-position: 200% 0; }
            100% { background-position: -200% 0; }
          }
          @keyframes pulse {
            0%, 100% { transform: scale(1); }
            50% { transform: scale(1.05); }
          }
          .shimmer {
            position: relative;
            overflow: hidden;
          }
          .shimmer::before {
            content: '';
            position: absolute;
            top: 0;
            left: 0;
            width: 100%;
            height: 100%;
            background: linear-gradient(90deg, transparent, rgba(255,255,255,0.4), transparent);
            animation: shimmer 2s infinite;
          }
          .pulse-animation {
            animation: pulse 2s ease-in-out infinite;
          }
        `}
            </style>
            <Container maxWidth="xl" sx={{ py: 4 }}>
                <Paper
                    elevation={8}
                    sx={{
                        borderRadius: 4,
                        overflow: 'hidden',
                        background: 'linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)',
                        boxShadow: '0 25px 50px rgba(0,0,0,0.15)',
                        border: '1px solid rgba(255,255,255,0.2)'
                    }}
                >
                    {/* Enhanced Professional Header Section */}
                    <Paper
                        elevation={0}
                        sx={{
                            background: 'linear-gradient(135deg, #2c3e50 0%, #3498db 100%)',
                            color: 'white',
                            borderRadius: 0,
                            p: { xs: 3, md: 5 },
                            position: 'relative',
                            overflow: 'hidden',
                            boxShadow: 'none'
                        }}
                    >
                        {/* Animated Background Pattern */}
                        <Box
                            sx={{
                                position: 'absolute',
                                top: 0,
                                left: 0,
                                right: 0,
                                bottom: 0,
                                opacity: 0.08,
                                backgroundImage: `
              radial-gradient(circle at 20% 20%, white 1px, transparent 1px),
              radial-gradient(circle at 80% 80%, white 1px, transparent 1px),
              radial-gradient(circle at 40% 40%, white 1px, transparent 1px)
            `,
                                backgroundSize: '60px 60px, 80px 80px, 40px 40px',
                                animation: 'float 20s ease-in-out infinite'
                            }}
                        />

                        {/* Floating Orbs */}
                        <Box
                            sx={{
                                position: 'absolute',
                                top: '10%',
                                right: '15%',
                                width: 100,
                                height: 100,
                                borderRadius: '50%',
                                background: 'rgba(255,255,255,0.1)',
                                backdropFilter: 'blur(10px)',
                                animation: 'float 15s ease-in-out infinite reverse'
                            }}
                        />
                        <Box
                            sx={{
                                position: 'absolute',
                                bottom: '20%',
                                left: '10%',
                                width: 60,
                                height: 60,
                                borderRadius: '50%',
                                background: 'rgba(255,255,255,0.08)',
                                backdropFilter: 'blur(8px)',
                                animation: 'float 12s ease-in-out infinite'
                            }}
                        />

                        <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" alignItems="center" sx={{ position: 'relative', zIndex: 1 }}>
                            <Box sx={{ flexGrow: 1, mb: { xs: 3, md: 0 } }}>
                                <Stack direction="row" alignItems="center" spacing={3} sx={{ mb: 2 }}>
                                    <Avatar sx={{
                                        bgcolor: 'rgba(255,255,255,0.25)',
                                        width: { xs: 56, md: 72 },
                                        height: { xs: 56, md: 72 },
                                        border: '3px solid rgba(255,255,255,0.4)',
                                        boxShadow: '0 8px 32px rgba(0,0,0,0.15)',
                                        backdropFilter: 'blur(10px)',
                                        transition: 'all 0.3s ease',
                                        '&:hover': {
                                            transform: 'scale(1.05) rotate(5deg)',
                                            boxShadow: '0 12px 40px rgba(0,0,0,0.2)'
                                        }
                                    }}>
                                        <DescriptionIcon sx={{ fontSize: { xs: 28, md: 36 }, color: 'white' }} />
                                    </Avatar>
                                    <Box>
                                        <Typography
                                            variant="h2"
                                            sx={{
                                                fontWeight: 900,
                                                mb: 0.5,
                                                fontSize: { xs: '2rem', sm: '2.5rem', md: '3.5rem' },
                                                textShadow: '0 4px 20px rgba(0,0,0,0.3)',
                                                background: 'linear-gradient(45deg, #ffffff 0%, #f0f8ff 100%)',
                                                backgroundClip: 'text',
                                                WebkitBackgroundClip: 'text',
                                                WebkitTextFillColor: 'transparent',
                                                letterSpacing: '-0.02em'
                                            }}
                                        >
                                            Platform Content
                                        </Typography>
                                        <Typography variant="h5" sx={{
                                            opacity: 0.95,
                                            fontWeight: 500,
                                            fontSize: { xs: '0.9rem', sm: '1rem', md: '1.3rem' },
                                            textShadow: '0 2px 10px rgba(0,0,0,0.2)',
                                            letterSpacing: '0.01em'
                                        }}>
                                            {hidesButtonText
                                                ? 'Control the frontend platform subtitles, URLs, and visibility. Button labels use the frontend defaults.'
                                                : 'Manage content and settings for different social platforms'}
                                        </Typography>
                                    </Box>
                                </Stack>
                            </Box>
                        </Stack>
                    </Paper>

                    {error && (
                        <Alert severity="error" sx={{ m: 3, borderRadius: 2 }} onClose={() => setError(null)}>
                            {error}
                        </Alert>
                    )}

                    {loading ? (
                        <Box display="flex" justifyContent="center" p={8}>
                            <CircularProgress size={40} thickness={4} />
                        </Box>
                    ) : (
                        <>
                            {/* Desktop View */}
                            <Box sx={{ display: { xs: 'none', md: 'block' } }}>
                                <TableContainer>
                                    <Table sx={{ minWidth: 650 }}>
                                        <TableHead>
                                            <TableRow sx={{ backgroundColor: '#f8f9fa' }}>
                                                <TableCell sx={{ fontWeight: 700, color: 'text.secondary', py: 2 }}>PLATFORM</TableCell>
                                                <TableCell sx={{ fontWeight: 700, color: 'text.secondary', py: 2 }}>CONTENT</TableCell>
                                                {hidesButtonText && <TableCell sx={{ fontWeight: 700, color: 'text.secondary', py: 2 }}>URL</TableCell>}
                                                {!hidesButtonText && <TableCell sx={{ fontWeight: 700, color: 'text.secondary', py: 2 }}>BUTTON TEXT</TableCell>}
                                                <TableCell sx={{ fontWeight: 700, color: 'text.secondary', py: 2 }}>STATUS</TableCell>
                                                <TableCell sx={{ fontWeight: 700, color: 'text.secondary', py: 2 }} align="right">ACTIONS</TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {contentList.length > 0 ? (
                                                contentList.map((item) => {
                                                    const platformStyle = getPlatformConfig(item.platform);
                                                    return (
                                                        <TableRow
                                                            key={item.id}
                                                            hover
                                                            sx={{
                                                                '&:last-child td, &:last-child th': { border: 0 },
                                                                transition: 'all 0.2s',
                                                                '&:hover': {
                                                                    backgroundColor: 'rgba(52, 152, 219, 0.04)',
                                                                    transform: 'translateY(-1px)',
                                                                    boxShadow: '0 2px 8px rgba(0,0,0,0.05)'
                                                                }
                                                            }}
                                                        >
                                                            <TableCell component="th" scope="row">
                                                                <Box display="flex" alignItems="center" gap={2}>
                                                                    <Avatar
                                                                        variant="rounded"
                                                                        sx={{
                                                                            bgcolor: platformStyle.bg,
                                                                            color: platformStyle.color,
                                                                            width: 40,
                                                                            height: 40,
                                                                            borderRadius: 3
                                                                        }}
                                                                    >
                                                                        {platformStyle.icon}
                                                                    </Avatar>
                                                                    <Typography variant="subtitle2" fontWeight="700" sx={{ color: '#2c3e50' }}>
                                                                        {item.platform}
                                                                    </Typography>
                                                                </Box>
                                                            </TableCell>
                                                            <TableCell sx={{ maxWidth: 350 }}>
                                                                <Typography variant="body1" noWrap sx={{ color: '#34495e', fontWeight: 500 }}>
                                                                    {item.content}
                                                                </Typography>
                                                            </TableCell>
                                                            {hidesButtonText && <TableCell sx={{ maxWidth: 320 }}>
                                                                <Typography
                                                                    variant="body2"
                                                                    noWrap
                                                                    title={String(item.platform || '').toLowerCase() === 'google' ? '' : (item.url || '')}
                                                                    sx={{ color: String(item.platform || '').toLowerCase() === 'google' || !item.url ? 'text.disabled' : '#34495e' }}
                                                                >
                                                                    {String(item.platform || '').toLowerCase() === 'google' ? 'Managed separately' : (item.url || 'Not configured')}
                                                                </Typography>
                                                            </TableCell>}
                                                            {!hidesButtonText && <TableCell>
                                                                <Chip
                                                                    label={item.button_text}
                                                                    size="small"
                                                                    sx={{
                                                                        bgcolor: '#f1f5f9',
                                                                        color: '#475569',
                                                                        fontWeight: 600,
                                                                        borderRadius: 2,
                                                                        fontFamily: 'Inter, sans-serif',
                                                                        border: '1px solid #e2e8f0'
                                                                    }}
                                                                />
                                                            </TableCell>}
                                                            <TableCell>
                                                                <Box display="flex" alignItems="center" gap={1}>
                                                                    <Box
                                                                        sx={{
                                                                            width: 8,
                                                                            height: 8,
                                                                            borderRadius: '50%',
                                                                            bgcolor: item.is_active ? '#2ecc71' : '#e74c3c',
                                                                            boxShadow: item.is_active ? '0 0 0 2px rgba(46, 204, 113, 0.2)' : 'none'
                                                                        }}
                                                                    />
                                                                    <Typography
                                                                        variant="caption"
                                                                        fontWeight="700"
                                                                        sx={{
                                                                            color: item.is_active ? '#27ae60' : '#c0392b',
                                                                            textTransform: 'uppercase',
                                                                            letterSpacing: '0.05em'
                                                                        }}
                                                                    >
                                                                        {item.is_active ? 'Active' : 'Inactive'}
                                                                    </Typography>
                                                                </Box>
                                                            </TableCell>
                                                            <TableCell align="right">
                                                                <IconButton
                                                                    onClick={() => handleEditClick(item)}
                                                                    size="small"
                                                                    sx={{
                                                                        color: '#3498db',
                                                                        bgcolor: 'rgba(52, 152, 219, 0.1)',
                                                                        borderRadius: 2,
                                                                        transition: 'all 0.2s',
                                                                        '&:hover': {
                                                                            bgcolor: '#3498db',
                                                                            color: 'white',
                                                                            transform: 'scale(1.1)'
                                                                        }
                                                                    }}
                                                                >
                                                                    <EditIcon fontSize="small" />
                                                                </IconButton>
                                                            </TableCell>
                                                        </TableRow>
                                                    );
                                                })
                                            ) : (
                                                <TableRow>
                                                    <TableCell colSpan={5} align="center" sx={{ py: 8 }}>
                                                        <Typography variant="h6" color="text.secondary" gutterBottom>
                                                            No content found
                                                        </Typography>
                                                    </TableCell>
                                                </TableRow>
                                            )}
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                            </Box>

                            {/* Mobile View */}
                            <Box sx={{ display: { xs: 'block', md: 'none' }, p: 2 }}>
                                <Stack spacing={2}>
                                    {contentList.map((item) => {
                                        const platformStyle = getPlatformConfig(item.platform);
                                        return (
                                            <Paper
                                                key={item.id}
                                                elevation={0}
                                                sx={{
                                                    p: 2,
                                                    border: '1px solid #e2e8f0',
                                                    borderRadius: 3,
                                                    transition: 'all 0.2s',
                                                    '&:active': {
                                                        transform: 'scale(0.98)',
                                                        bgcolor: '#f8fafc'
                                                    }
                                                }}
                                            >
                                                <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={2}>
                                                    <Box display="flex" alignItems="center" gap={2}>
                                                        <Avatar
                                                            variant="rounded"
                                                            sx={{
                                                                bgcolor: platformStyle.bg,
                                                                color: platformStyle.color,
                                                                width: 40,
                                                                height: 40,
                                                                borderRadius: 3
                                                            }}
                                                        >
                                                            {platformStyle.icon}
                                                        </Avatar>
                                                        <Box>
                                                            <Typography variant="subtitle1" fontWeight="700" sx={{ color: '#2c3e50' }}>
                                                                {item.platform}
                                                            </Typography>
                                                            <Box display="flex" alignItems="center" gap={1}>
                                                                <Box
                                                                    sx={{
                                                                        width: 6,
                                                                        height: 6,
                                                                        borderRadius: '50%',
                                                                        bgcolor: item.is_active ? '#2ecc71' : '#e74c3c'
                                                                    }}
                                                                />
                                                                <Typography
                                                                    variant="caption"
                                                                    fontWeight="600"
                                                                    sx={{
                                                                        color: item.is_active ? '#27ae60' : '#c0392b',
                                                                        textTransform: 'uppercase'
                                                                    }}
                                                                >
                                                                    {item.is_active ? 'Active' : 'Inactive'}
                                                                </Typography>
                                                            </Box>
                                                        </Box>
                                                    </Box>
                                                    <IconButton
                                                        onClick={() => handleEditClick(item)}
                                                        size="small"
                                                        sx={{
                                                            color: '#3498db',
                                                            bgcolor: 'rgba(52, 152, 219, 0.1)',
                                                            borderRadius: 2
                                                        }}
                                                    >
                                                        <EditIcon fontSize="small" />
                                                    </IconButton>
                                                </Box>

                                                <Box mb={2}>
                                                    <Typography variant="caption" color="text.secondary" fontWeight="600" display="block" mb={0.5}>
                                                        CONTENT
                                                    </Typography>
                                                    <Typography variant="body2" sx={{ color: '#34495e', fontWeight: 500 }}>
                                                        {item.content}
                                                    </Typography>
                                                </Box>

                                                {hidesButtonText && <Box mb={2}>
                                                    <Typography variant="caption" color="text.secondary" fontWeight="600" display="block" mb={0.5}>
                                                        URL
                                                    </Typography>
                                                    <Typography variant="body2" noWrap sx={{ color: String(item.platform || '').toLowerCase() === 'google' || !item.url ? 'text.disabled' : '#34495e' }}>
                                                        {String(item.platform || '').toLowerCase() === 'google' ? 'Managed separately' : (item.url || 'Not configured')}
                                                    </Typography>
                                                </Box>}

                                                {!hidesButtonText && <Box>
                                                    <Typography variant="caption" color="text.secondary" fontWeight="600" display="block" mb={0.5}>
                                                        BUTTON TEXT
                                                    </Typography>
                                                    <Chip
                                                        label={item.button_text}
                                                        size="small"
                                                        sx={{
                                                            bgcolor: '#f1f5f9',
                                                            color: '#475569',
                                                            fontWeight: 600,
                                                            borderRadius: 2,
                                                            fontFamily: 'Inter, sans-serif',
                                                            border: '1px solid #e2e8f0'
                                                        }}
                                                    />
                                                </Box>}
                                            </Paper>
                                        );
                                    })}
                                </Stack>
                            </Box>
                        </>
                    )}
                </Paper>

                {/* Edit Dialog */}
                <Dialog
                    open={editDialogOpen}
                    onClose={() => setEditDialogOpen(false)}
                    maxWidth="sm"
                    fullWidth
                    PaperProps={{
                        sx: {
                            borderRadius: { xs: 0, sm: 4 },
                            overflow: 'hidden',
                            maxWidth: 680,
                            boxShadow: '0 28px 80px rgba(15, 23, 42, 0.28)'
                        }
                    }}
                >
                    <DialogTitle sx={{ p: 0 }}>
                        <Box
                            sx={{
                                p: { xs: 2.5, sm: 3 },
                                color: 'white',
                                background: 'linear-gradient(135deg, #172a3a 0%, #244b68 55%, #2f80b7 100%)',
                                display: 'flex',
                                alignItems: 'center',
                                gap: 2
                            }}
                        >
                            {editingItem && (
                                <Avatar
                                    variant="rounded"
                                    sx={{
                                        width: 52,
                                        height: 52,
                                        borderRadius: 3,
                                        bgcolor: 'rgba(255,255,255,0.96)',
                                        color: getPlatformConfig(editingItem.platform).color,
                                        boxShadow: '0 8px 24px rgba(0,0,0,0.18)'
                                    }}
                                >
                                    {getPlatformConfig(editingItem.platform).icon}
                                </Avatar>
                            )}
                            <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                                <Typography variant="overline" sx={{ opacity: 0.72, fontWeight: 700, letterSpacing: '0.12em', lineHeight: 1 }}>
                                    Frontend platform
                                </Typography>
                                <Typography variant="h5" component="div" sx={{ fontWeight: 750, mt: 0.5 }}>
                                    {editingItem ? `Edit ${editingItem.platform}` : 'Edit platform'}
                                </Typography>
                                <Typography variant="body2" sx={{ opacity: 0.78, mt: 0.5 }}>
                                    Update what visitors see and where the card takes them.
                                </Typography>
                            </Box>
                            <IconButton
                                aria-label="Close editor"
                                onClick={() => setEditDialogOpen(false)}
                                sx={{ color: 'white', bgcolor: 'rgba(255,255,255,0.12)', '&:hover': { bgcolor: 'rgba(255,255,255,0.2)' } }}
                            >
                                <CloseIcon />
                            </IconButton>
                        </Box>
                    </DialogTitle>
                    <DialogContent sx={{ p: { xs: 2.5, sm: 3 }, bgcolor: '#f8fafc' }}>
                        {editingItem && (
                            <Stack spacing={2.5}>
                                <Box>
                                    <Typography variant="subtitle2" fontWeight="700" sx={{ color: '#1e293b', mb: 1 }}>
                                        Card subtitle
                                    </Typography>
                                    <TextField
                                        value={editingItem.content}
                                        onChange={(e) => setEditingItem({ ...editingItem, content: e.target.value })}
                                        multiline
                                        minRows={2}
                                        maxRows={3}
                                        fullWidth
                                        placeholder="Enter the short description shown below the platform name"
                                        inputProps={{ maxLength: 50 }}
                                        helperText={`${(editingItem.content || '').length}/50 characters`}
                                        FormHelperTextProps={{ sx: { textAlign: 'right', mr: 0 } }}
                                        sx={{ '& .MuiOutlinedInput-root': { bgcolor: 'white', borderRadius: 2.5 } }}
                                    />
                                </Box>

                                {hidesButtonText && String(editingItem.platform || '').toLowerCase() !== 'google' && (
                                    <Box>
                                        <Typography variant="subtitle2" fontWeight="700" sx={{ color: '#1e293b', mb: 1 }}>
                                            Destination URL
                                        </Typography>
                                        <TextField
                                            value={editingItem.url || ''}
                                            onChange={(e) => setEditingItem({ ...editingItem, url: e.target.value })}
                                            fullWidth
                                            placeholder="https://example.com/profile"
                                            helperText="Visitors open this link when they select the platform."
                                            sx={{ '& .MuiOutlinedInput-root': { bgcolor: 'white', borderRadius: 2.5 } }}
                                        />
                                    </Box>
                                )}

                                {hidesButtonText && String(editingItem.platform || '').toLowerCase() === 'google' && (
                                    <Paper variant="outlined" sx={{ p: 2, borderRadius: 2.5, bgcolor: '#f1f5f9', borderColor: '#dbe4ee' }}>
                                        <Typography variant="subtitle2" fontWeight="700" sx={{ color: '#334155' }}>Google review locations</Typography>
                                        <Typography variant="body2" sx={{ color: '#64748b', mt: 0.5 }}>
                                            Review URLs are managed separately and are not shown in this editor.
                                        </Typography>
                                    </Paper>
                                )}

                                {!hidesButtonText && (
                                    <TextField
                                        label="Button Text"
                                        value={editingItem.button_text || ''}
                                        onChange={(e) => setEditingItem({ ...editingItem, button_text: e.target.value })}
                                        fullWidth
                                        helperText="Text displayed on the action button"
                                        sx={{ '& .MuiOutlinedInput-root': { bgcolor: 'white', borderRadius: 2.5 } }}
                                    />
                                )}

                                <Paper
                                    variant="outlined"
                                    sx={{
                                        p: 2.25,
                                        borderRadius: 2.5,
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'space-between',
                                        borderColor: editingItem.is_active ? '#a7e3c2' : '#dbe4ee',
                                        bgcolor: editingItem.is_active ? '#f0fdf6' : 'white',
                                        transition: 'all 0.2s ease'
                                    }}
                                >
                                    <Box>
                                        <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 0.5 }}>
                                            <Typography variant="subtitle2" fontWeight="700" sx={{ color: '#1e293b' }}>Frontend visibility</Typography>
                                            <Chip
                                                label={editingItem.is_active ? 'Visible' : 'Hidden'}
                                                size="small"
                                                sx={{
                                                    height: 22,
                                                    fontWeight: 700,
                                                    bgcolor: editingItem.is_active ? '#d9f7e6' : '#eef2f6',
                                                    color: editingItem.is_active ? '#18794e' : '#64748b'
                                                }}
                                            />
                                        </Stack>
                                        <Typography variant="body2" sx={{ color: '#64748b' }}>
                                            {editingItem.is_active ? 'Visitors can see this platform card.' : 'This platform card is hidden from visitors.'}
                                        </Typography>
                                    </Box>
                                    <Switch
                                        checked={editingItem.is_active}
                                        onChange={(e) => setEditingItem({ ...editingItem, is_active: e.target.checked })}
                                        color="success"
                                        inputProps={{ 'aria-label': 'Frontend visibility' }}
                                    />
                                </Paper>
                            </Stack>
                        )}
                    </DialogContent>
                    <DialogActions sx={{ px: { xs: 2.5, sm: 3 }, py: 2.25, borderTop: '1px solid #e2e8f0', bgcolor: 'white' }}>
                        <Button
                            onClick={() => setEditDialogOpen(false)}
                            color="inherit"
                            sx={{ borderRadius: 2.5, textTransform: 'none', fontWeight: 700, px: 2.5, color: '#475569' }}
                        >
                            Cancel
                        </Button>
                        <Button
                            onClick={handleSave}
                            variant="contained"
                            startIcon={saving ? <CircularProgress size={20} color="inherit" /> : <SaveIcon />}
                            disabled={saving}
                            sx={{
                                borderRadius: 2.5,
                                textTransform: 'none',
                                fontWeight: 700,
                                px: 3,
                                py: 1,
                                bgcolor: '#203a4f',
                                boxShadow: '0 8px 20px rgba(32,58,79,0.22)',
                                '&:hover': { bgcolor: '#172a3a', boxShadow: '0 10px 24px rgba(32,58,79,0.3)' }
                            }}
                        >
                            Save Changes
                        </Button>
                    </DialogActions>
                </Dialog>

            </Container>
        </>
    );
};

export default PlatformContent;
